/**
 * Retailer & Wholesale Agreements — 10 documents.
 *
 * The purchase order is not the deal. The vendor agreement, the deduction policy and the routing
 * guide are the deal — and the useful skill is modelling true net before accepting the PO.
 */
export const RETAIL_DOCS = [
  {
    id: "wholesale-supply-agreement",
    name: "Wholesale Supply Agreement",
    category: "retail",
    subcategory: "Core supply terms",
    aliases: [
      "Wholesale Terms of Sale",
      "Supply Terms",
      "Wholesale Trading Terms",
      "Supplier Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Retailer or Wholesale Buyer",
    whenYouNeedIt:
      "Before you take your first wholesale order from a shop, a chain or a wholesaler. If you are currently selling on an emailed price list and a purchase order, this is the document that is missing.",
    whatItDoes:
      "Sets the standing commercial terms behind every order: your price and how it changes, order minimums, payment and credit, delivery and title, packaging and labelling standards, and what happens when product is late, short, damaged or rejected.",
    whyItMatters:
      "Without standing terms, every order runs on whatever is printed on the buyer's purchase order — and a retailer's PO terms are written entirely for the retailer. That means their payment terms, their right to reject, their return rights and their deduction schedule apply by default. Setting your own wholesale terms early is the cheapest leverage you will ever have, because once you are a small vendor at a large chain the terms stop being negotiable at all.",
    majorTerms: [
      "Wholesale price list, tiers and price change notice",
      "Minimum order quantity and minimum order value",
      "Payment terms, credit limit and stop-ship rights",
      "Delivery terms, title transfer and risk of loss",
      "Lead times and order acceptance",
      "Fill rate and short-shipment handling",
      "Case pack, pallet configuration and labelling standards",
      "Minimum remaining shelf life at delivery",
      "Inspection, rejection and claims window",
      "Returns policy and restocking",
      "Deduction and chargeback procedure",
      "Insurance, indemnity and product liability",
      "Term and termination",
    ],
    clausesToWatch: [
      {
        clause: "Which terms govern: yours or the purchase order's",
        why: "If your agreement does not say it prevails over the buyer's PO and portal terms, the buyer's terms usually end up governing every order you ship.",
      },
      {
        clause: "Deduction and chargeback procedure",
        why: "A right to deduct without documentation and without a dispute window means disputes are settled by whoever holds the money, which is never you.",
      },
      {
        clause: "Payment terms and credit limit",
        why: "Long terms on a large opening order can consume the cash from an entire production run before a single invoice is paid.",
      },
      {
        clause: "Returns and rejection rights",
        why: "An unlimited right to return unsold or slow product turns a booked sale into inventory that comes back short-coded and unsaleable.",
      },
      {
        clause: "Price change notice",
        why: "A long notice requirement with a right to buy ahead lets a buyer load up before every increase, so your cost increase lands months before your price increase does.",
      },
      {
        clause: "Minimum remaining shelf life",
        why: "Undefined, this becomes the buyer's judgement call, and product can be rejected on arrival for a code the buyer never told you about.",
      },
      {
        clause: "Indemnity and insurance requirements",
        why: "Broad indemnities and high insurance limits demanded by a retailer can exceed what your policy actually covers, leaving the gap with you.",
      },
    ],
    cpgIssues: [
      "Minimum remaining shelf life at receipt is one of the most common causes of rejected deliveries and should be stated in days or as a share of total shelf life, agreed in writing.",
      "Case pack, tie-high configuration, pallet type and case markings have to match the buyer's receiving standard, or you pay handling penalties on arrival.",
      "Labelling requirements including barcodes, case labels, lot codes and date formats are all specified by the buyer, and non-conforming labelling is a deduction on every pallet.",
      "Lot coding and traceability records need to be kept on your side so a withdrawal can identify exactly which shipments went where.",
      "Product liability insurance limits demanded by retailers can be significant, and being named as additional insured is usually required; check what your policy actually supports before promising it.",
      "Temperature and storage requirements in transit and at the buyer's site should be stated for chilled or sensitive product, since a quality failure at the shelf carries your name.",
      "Fill rate expectations start applying immediately, and a small brand short-shipping a chain accumulates service penalties faster than it accumulates revenue.",
    ],
    founderMistakes: [
      "Selling on the buyer's purchase order terms for two years without ever issuing their own wholesale terms, and inheriting a deduction schedule they never agreed to.",
      "Accepting long payment terms on a large opening order and running out of cash before the invoice cleared.",
      "Not stating minimum remaining shelf life, and having a full truck rejected on arrival for short dating.",
      "Promising insurance limits demanded by a retailer without checking the policy, and discovering the coverage gap only after a claim.",
      "Leaving returns open-ended and receiving unsold, out-of-code product back at their own freight cost.",
    ],
    negotiationPoints: [
      "State that your terms prevail over any conflicting purchase order or vendor portal terms.",
      "Set a credit limit and a right to stop shipping on overdue balances, and require prepayment for opening orders from new buyers.",
      "Define minimum remaining shelf life at delivery in writing.",
      "Limit returns to defective or non-conforming product within a stated claim window.",
      "Require documentation and a dispute window before any deduction is taken.",
      "Cap the buy-ahead quantity before an announced price increase.",
      "Check the required insurance limits against your actual policy before agreeing them.",
    ],
    redFlags: [
      "Silence on which terms govern when your terms and the buyer's PO conflict.",
      "An unlimited right of return for unsold product.",
      "Deductions permitted with no documentation and no dispute process.",
      "No stated minimum remaining shelf life.",
      "Payment terms you cannot fund at the order size being discussed.",
      "An uncapped indemnity, or insurance requirements you have not verified against your policy.",
      "Acceptance and rejection standards left entirely to the buyer's discretion.",
    ],
    beforeYouSign: [
      "Your own written wholesale terms and price list, before the first order rather than after.",
      "A cash-flow model at the order size and payment terms being proposed.",
      "Your certificate of insurance and confirmation of what limits and endorsements you actually carry.",
      "The buyer's receiving standard: case pack, pallet spec, labelling and minimum code requirements.",
    ],
    related: [
      "retail-vendor-agreement",
      "retailer-supply-agreement",
      "purchase-order-terms",
      "wholesale-distribution-agreement",
      "food-distributor-agreement",
    ],
    attorneyReview: "yes",
    templateId: "wholesale-supply-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "retail-vendor-agreement",
    name: "Retail Vendor Agreement",
    category: "retail",
    subcategory: "Core supply terms",
    aliases: [
      "Vendor Agreement",
      "Supplier Agreement",
      "Vendor Terms and Conditions",
      "Vendor Compliance Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Retailer",
    whenYouNeedIt:
      "The moment a retailer accepts your item and sends you onboarding paperwork. The vendor agreement, the vendor portal terms, the deduction policy and the routing guide all arrive together and all of them are binding.",
    whatItDoes:
      "Sets the entire operating relationship with a retailer: pricing and allowances, ordering and delivery compliance, invoicing and payment, the fees and deductions that will be taken from your invoices, insurance and indemnity, and the retailer's rights to change items, quantities and terms.",
    whyItMatters:
      "The purchase order is not the deal. The vendor agreement, the deduction policy and the routing guide are the deal, and they are usually where a profitable-looking order becomes a loss. For a small brand at a large retailer these documents are largely non-negotiable — the buyer has no authority to change them and asking for redlines mostly costs you goodwill. So the useful skill is not redlining: it is reading the deduction schedule and the routing guide carefully enough to model your true net per case before you accept the purchase order, and being willing to decline volume that does not clear.",
    majorTerms: [
      "Cost of goods, price change process and price protection",
      "The deduction and allowance schedule in full",
      "Slotting, new-item and warehouse fees",
      "Promotional allowances and their funding basis",
      "Freight terms, routing guide compliance and freight deductions",
      "Order compliance: fill rate, on-time delivery and service penalties",
      "Invoicing, EDI requirements and payment terms",
      "Returns, spoilage, markdown and damage allowances",
      "New-store and remodel allowances",
      "Insurance limits and additional insured status",
      "Indemnity, product liability and recall obligations",
      "Item discontinuation and inventory disposition",
      "Audit and post-audit deduction rights",
      "Term, amendment and the retailer's right to change terms",
    ],
    clausesToWatch: [
      {
        clause: "Incorporation of the routing guide and vendor manual by reference",
        why: "These documents are longer than the agreement, change without negotiation, and contain most of the penalties, so agreeing to them by reference means agreeing to something you have not read.",
      },
      {
        clause: "The retailer's right to amend terms",
        why: "A unilateral right to update the fee schedule or vendor manual means the economics you modelled can change after you have committed inventory.",
      },
      {
        clause: "Post-audit rights",
        why: "A right to audit and deduct for periods long past means a deduction can arrive years after the sale, against an invoice you closed and a season you already spent.",
      },
      {
        clause: "On-time and in-full penalties",
        why: "Service penalties are charged per order regardless of order size, so a small brand shipping small orders can pay penalties out of proportion to the revenue.",
      },
      {
        clause: "Markdown and spoilage allowances",
        why: "An allowance funding the retailer's markdown on product that did not sell converts your revenue into their clearance budget.",
      },
      {
        clause: "Indemnity and insurance",
        why: "Broad indemnity language covering the retailer's own conduct, combined with insurance limits you have not verified, leaves exposure sitting with you.",
      },
      {
        clause: "Item discontinuation and inventory disposition",
        why: "A right to return all remaining warehouse and store stock on discontinuation, freight collect, can make being delisted more expensive than never being listed.",
      },
      {
        clause: "Deduction dispute window",
        why: "A short window to dispute, combined with slow deduction backup, means invalid deductions become permanent simply by running out the clock.",
      },
    ],
    cpgIssues: [
      "Slotting and new-item fees are cash out before a single case sells, and they are usually charged whether or not the item performs.",
      "Free fills for a full chain rollout can be an entire production run given away, so calculate the case count across every store before agreeing.",
      "Promotional allowances, scan-downs and ad features are deducted after the fact, often two or three cycles later, which is why brands routinely mis-forecast cash.",
      "Spoilage and unsaleable allowances taken as a flat percentage hit short-shelf-life items hardest and should be modelled at the actual rate for your category.",
      "Routing guide compliance covers appointment scheduling, pallet configuration, case labelling, ASN accuracy and carrier selection, and each one carries its own penalty schedule.",
      "New-store and remodel allowances mean each new store opening costs you product and cash, which is easy to forget when the chain is expanding.",
      "Recall and withdrawal obligations in vendor agreements can require you to fund retailer labour, disposal, customer notification and lost margin, well beyond the value of the product itself.",
      "Fill rate is measured from the moment you are live, and a co-packer scheduling delay becomes a retailer service penalty with no allowance for being small.",
    ],
    founderMistakes: [
      "Modelling the deal on the cost of goods the buyer agreed and never reading the deduction schedule, then discovering true net per case was far below production cost.",
      "Accepting a full chain rollout without calculating the free fill across every store, and consuming an entire production run on day one.",
      "Ignoring the routing guide and paying compliance penalties on nearly every early shipment for pallet, labelling and appointment errors.",
      "Missing a short deduction dispute window because the backup documentation arrived late, and writing off valid claims.",
      "Being delisted after a year and receiving a full return of warehouse stock, freight collect, that exceeded the profit on the entire relationship.",
      "Agreeing insurance and indemnity requirements without checking coverage, and finding a gap when a claim arrived.",
    ],
    negotiationPoints: [
      "Ask for the complete fee, deduction and allowance schedule and the routing guide before you accept the first purchase order, and model your net from them.",
      "Where anything is negotiable at all, prioritise the deduction dispute window and the free fill scope over the case price.",
      "Ask for a phased rollout by region rather than a full chain launch, so free fill and fill-rate risk are staged.",
      "Ask for a written cap or a stated basis on markdown and spoilage allowances rather than open-ended deductions.",
      "Confirm your insurance limits and additional insured endorsement before agreeing the insurance clause.",
      "Get the item discontinuation and inventory disposition process in writing before you launch, not after you are delisted.",
      "Be prepared to decline volume that does not clear your net threshold; a purchase order you cannot service profitably is not a win.",
    ],
    redFlags: [
      "Being asked to sign without receiving the routing guide, vendor manual and deduction schedule.",
      "An unrestricted right for the retailer to amend fees and terms at any time.",
      "Post-audit deduction rights extending years back with no cap.",
      "A deduction dispute window shorter than the time it takes to obtain backup documentation.",
      "Open-ended markdown, spoilage or destroy-in-field allowances.",
      "Full return of all warehouse and store inventory on discontinuation at your cost.",
      "Indemnity covering the retailer's own acts, or insurance limits you have not verified.",
      "A rollout scope whose free fill exceeds what you can produce and fund.",
    ],
    beforeYouSign: [
      "The complete deduction and allowance schedule, the routing guide and the vendor manual, read in full.",
      "A modelled true net per case with every fee, allowance, deduction and freight charge loaded in.",
      "The free fill case count across every store in the proposed rollout, priced at your cost of goods.",
      "Confirmation from your co-packer and 3PL that they can hold your fill rate and routing compliance at this volume.",
      "Your certificate of insurance checked against the required limits and endorsements.",
    ],
    related: [
      "wholesale-supply-agreement",
      "slotting-placement-agreement",
      "promotional-allowance-agreement",
      "retailer-supply-agreement",
      "retail-marketing-agreement",
      "food-distributor-agreement",
    ],
    attorneyReview: "yes",
    templateId: "retail-vendor-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "retailer-supply-agreement",
    name: "Retailer Supply Agreement",
    category: "retail",
    subcategory: "Core supply terms",
    aliases: [
      "Supply Agreement (Retail)",
      "Retail Supply Contract",
      "Service Level Supply Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Retailer",
    whenYouNeedIt:
      "When a retailer wants a supply commitment rather than order-by-order buying: guaranteed availability, a forecast you will hold capacity against, or a contracted price for a stated period.",
    whatItDoes:
      "Commits you to supply agreed volumes at agreed prices over a period, and sets the service levels, forecasting process, lead times and consequences that go with that commitment.",
    whyItMatters:
      "A supply commitment moves risk from the retailer onto you. You are agreeing to have product available on a forecast you do not control, at a price fixed while your ingredient and freight costs are not, with penalties if you cannot deliver. For a brand with one co-packer and long ingredient lead times, that is a genuine operational exposure — and the clause that saves you is a realistic forecast and change-notice process, not a better price.",
    majorTerms: [
      "Committed volumes and the forecasting process",
      "Whether the forecast is binding or indicative",
      "Contract price and the price adjustment mechanism",
      "Lead times and order change windows",
      "Fill rate and on-time service levels",
      "Consequences of short supply and service failure",
      "Safety stock or inventory holding obligations",
      "Allocation rules if you cannot supply everyone",
      "Force majeure and supply interruption",
      "Quality specification and acceptance",
      "Term, renewal and termination",
    ],
    clausesToWatch: [
      {
        clause: "Binding versus indicative forecast",
        why: "A forecast that binds you to supply but does not bind the retailer to buy leaves you holding ingredients and packaging bought against volume that never materialises.",
      },
      {
        clause: "Price adjustment mechanism",
        why: "A fixed price for a long period with no pass-through for documented ingredient, packaging or freight increases turns an input cost spike straight into a loss.",
      },
      {
        clause: "Order change window",
        why: "A right to change quantities inside your production lead time is a promise you cannot keep, because your co-packer's schedule and your ingredient orders are already committed.",
      },
      {
        clause: "Service level consequences",
        why: "Penalties or a right to cover-purchase at your expense can exceed the margin on the order that was missed.",
      },
      {
        clause: "Allocation during shortage",
        why: "Without an allocation clause you can be contractually required to supply one customer in full while defaulting on everyone else.",
      },
      {
        clause: "Force majeure scope",
        why: "A narrow definition that excludes co-packer failure or ingredient shortage leaves the most likely causes of your supply failure outside the protection.",
      },
    ],
    cpgIssues: [
      "Your true lead time is your co-packer's production slot plus ingredient and packaging lead times, which can be months; commit to a change window that reflects that, not the retailer's ordering rhythm.",
      "Can, bottle, label and carton minimums mean a forecast change strands packaging you already bought, so the forecast obligation should carry some protection for committed materials.",
      "A single co-packer with no qualified backup is a real supply risk; be honest about it in the force majeure and service level clauses rather than promising continuity you cannot deliver.",
      "Shelf life limits how much safety stock you can hold, so a safety stock obligation on a short-coded product creates guaranteed write-offs.",
      "Ingredient price volatility in agricultural and functional inputs is the norm, so a fixed price with no pass-through mechanism is a bet on commodity markets.",
      "Allergen changeovers and line scheduling at a shared co-packing facility constrain how quickly you can add a run, which matters when a retailer wants an emergency order.",
      "Specification and acceptance criteria should reference your signed spec sheet, so a batch is not rejected against a standard nobody wrote down.",
    ],
    founderMistakes: [
      "Accepting a binding supply commitment against a non-binding retailer forecast, and holding six figures of packaging when the forecast halved.",
      "Fixing a price for a year while an ingredient tripled, with no pass-through mechanism.",
      "Promising a lead time shorter than their co-packer could schedule, and paying service penalties from the first quarter.",
      "Agreeing a safety stock obligation on a short-shelf-life product and writing off aged inventory every cycle.",
      "Writing a force majeure clause that did not cover co-packer failure, which is the most likely thing to go wrong.",
    ],
    negotiationPoints: [
      "Make forecasts indicative for the retailer and binding only inside a window that matches your real production lead time.",
      "Include a documented pass-through mechanism for ingredient, packaging and freight cost increases, with notice.",
      "Set the order change window to your actual co-packer and materials lead time, not the retailer's preference.",
      "Ask for an allocation clause allowing pro-rata supply during shortage.",
      "Extend force majeure to cover co-packer failure, ingredient shortage and packaging supply interruption.",
      "Cap service level consequences and exclude failures caused by the retailer's own forecast changes.",
    ],
    redFlags: [
      "A binding supply obligation matched to a non-binding purchase forecast.",
      "A fixed price for a long term with no pass-through for input cost increases.",
      "A right to change order quantities inside your production lead time.",
      "Uncapped service penalties or an unrestricted right to cover-purchase at your cost.",
      "No allocation provision for shortage.",
      "Force majeure that excludes supply chain and manufacturing failure.",
    ],
    beforeYouSign: [
      "Written confirmation from your co-packer of the production capacity and lead time you are about to promise.",
      "Ingredient and packaging lead times and minimum order quantities for the committed volume.",
      "A cost model showing what happens to margin if key inputs move against you during the term.",
      "Your signed product specification sheet, referenced in the acceptance clause.",
    ],
    related: [
      "retail-vendor-agreement",
      "wholesale-supply-agreement",
      "co-packer-agreement",
      "master-supply-agreement",
      "product-specification-sheet",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "consignment-agreement",
    name: "Consignment Agreement",
    category: "retail",
    subcategory: "Consignment and returns",
    aliases: [
      "Consignment Sales Agreement",
      "Goods on Consignment",
      "Consigned Inventory Agreement",
    ],
    stages: ["launch", "distribution"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand (Consignor) + Retailer (Consignee)",
    whenYouNeedIt:
      "When a retailer will stock your product but only wants to pay for what sells. Common with independent shops, specialty retail, new-brand programs and pop-ups, and often the only way into a store that will not take inventory risk.",
    whatItDoes:
      "Places your product in a retailer's store while you keep ownership of it. The retailer sells it, keeps an agreed share and remits the rest; unsold product remains yours and comes back. Title does not pass until the product sells to a consumer.",
    whyItMatters:
      "Consignment looks generous and costs more than founders expect, because you are funding inventory sitting on someone else's shelf. You do not have a sale when the product ships, so the revenue is not yours to recognise and the cash does not arrive until a consumer buys. You also carry the risk of that inventory: shrinkage, damage, product ageing out of code, and exposure if the retailer runs into financial trouble while holding goods you still own. Protecting consigned goods against a retailer's creditors involves steps that vary by situation and belong with counsel before the first delivery, not after a problem.",
    majorTerms: [
      "Statement that title remains with the brand until sale to a consumer",
      "Revenue split or consignment commission",
      "Reporting and remittance schedule",
      "Physical inventory counts and reconciliation",
      "Responsibility for shrinkage, theft and damage",
      "Insurance on consigned inventory and who carries it",
      "Product ageing, code dates and rotation",
      "Return of unsold inventory and who pays freight",
      "Retail price setting and markdown authority",
      "Steps to protect the brand's interest in the goods",
      "Term, termination and inventory recovery",
    ],
    clausesToWatch: [
      {
        clause: "Title and ownership statement",
        why: "If the agreement does not clearly say the goods remain yours until sold to a consumer, the whole arrangement can be read as an ordinary sale with delayed payment.",
      },
      {
        clause: "Shrinkage and damage responsibility",
        why: "Product that goes missing from the shelf is your inventory, and unless the retailer is responsible for shrinkage you fund every loss.",
      },
      {
        clause: "Protection of your interest in the goods",
        why: "Consigned goods can be exposed if the retailer runs into financial difficulty, and the steps needed to protect your position vary by situation and should be settled with counsel first.",
      },
      {
        clause: "Reporting and remittance",
        why: "Without a stated reporting cadence and reconciliation right, you are relying entirely on the retailer's account of what sold.",
      },
      {
        clause: "Markdown authority",
        why: "A retailer that can mark down your product at will is discounting inventory you still own and reducing your remittance without your agreement.",
      },
      {
        clause: "Return of unsold inventory",
        why: "If freight and handling on the return are yours, slow-moving product costs you twice: once in cash to produce it and again to get it back.",
      },
      {
        clause: "Inventory count rights",
        why: "Without a right to count the shelf, your inventory records and theirs will diverge and there is no way to settle the difference.",
      },
    ],
    cpgIssues: [
      "Shelf life is the hidden cost of consignment — product ageing on a shelf you own comes back to you short-coded or unsaleable, so agree a rotation standard and a pull date.",
      "Code date rotation should be the retailer's obligation even though the product is yours, or your oldest stock will sit behind the newest.",
      "Chilled and frozen consigned product carries cold-chain risk on someone else's equipment; specify storage conditions and who bears loss from a failure.",
      "Revenue recognition changes materially under consignment: shipping into the store is not a sale, which affects your reported revenue, your investor reporting and any lender covenants tied to sales.",
      "Cash conversion is slower than any other channel because you fund production, freight and shelf inventory before a single consumer transaction, so model working capital before agreeing a large placement.",
      "Insurance on inventory held at someone else's site is not automatic; check whether your policy covers goods off your premises.",
      "Damaged and opened product on a self-service shelf is a recurring loss on food and beverage that does not exist in categories with less handling.",
    ],
    founderMistakes: [
      "Treating consigned shipments as sales in their reporting, then having to restate revenue when the accounting was reviewed.",
      "Placing a large consigned inventory into several stores and running out of cash, because production was funded and nothing had sold.",
      "Not agreeing a rotation or pull date, and receiving the entire placement back out of code and unsaleable.",
      "Carrying shrinkage losses on product that disappeared from shelves, because responsibility was never assigned.",
      "Discovering that their insurance did not extend to inventory held at third-party locations after a loss.",
      "Never taking the steps counsel would have recommended to protect their interest in the goods, and having no clear position when a retailer got into difficulty.",
    ],
    negotiationPoints: [
      "State expressly that title and risk remain with you until sale to the consumer, and that the goods are held on consignment.",
      "Make the retailer responsible for shrinkage and for damage caused by their handling.",
      "Set a reporting and remittance cadence with a right to inspect and count inventory.",
      "Agree a rotation standard and a pull date, and require the retailer to sell oldest stock first.",
      "Require your approval before any markdown of consigned product.",
      "Agree who pays return freight, and cap the total inventory you will place at one time.",
      "Take counsel's advice on the steps needed to protect your interest in the goods before the first delivery.",
    ],
    redFlags: [
      "No clear statement that title remains with you until sale.",
      "Shrinkage and damage treated as your loss with no retailer responsibility.",
      "No reporting cadence, reconciliation right or inventory count right.",
      "Unilateral markdown authority on inventory you own.",
      "No pull date or rotation obligation on a coded product.",
      "Return freight and handling entirely at your cost.",
      "A placement size larger than you can fund while it sits unsold.",
    ],
    beforeYouSign: [
      "A working capital model showing what the placement costs you to fund while it sits on shelf.",
      "Confirmation from your insurer that inventory at third-party locations is covered.",
      "Counsel's advice on protecting your interest in goods held by the retailer.",
      "An agreed rotation standard, pull date and return process in writing.",
    ],
    related: [
      "sale-or-return-agreement",
      "retail-vendor-agreement",
      "wholesale-supply-agreement",
      "warehouse-agreement",
      "retailer-supply-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "You are leaving goods you still own in someone else's possession, and protecting that ownership against the retailer's creditors involves steps that should be taken before the first delivery.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "sale-or-return-agreement",
    name: "Sale-or-Return Agreement",
    category: "retail",
    subcategory: "Consignment and returns",
    aliases: [
      "Guaranteed Sale Agreement",
      "Sale or Return Terms",
      "Returnable Sale Agreement",
    ],
    stages: ["launch", "distribution"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Retailer",
    whenYouNeedIt:
      "When a retailer will buy your product outright but wants the right to send back whatever does not sell, often as the condition of taking a new item or a seasonal launch.",
    whatItDoes:
      "Structures a sale where title passes to the retailer but a return right sits on top of it. The retailer buys and pays, and can return unsold product within an agreed window on agreed terms.",
    whyItMatters:
      "This sits between an outright sale and consignment, and the return right is what determines which one it actually resembles. An unlimited return window makes a booked sale provisional: the revenue can reverse, the cash can be clawed back through deductions, and the product comes back short-coded and often unsaleable. What you get in exchange is distribution you would not otherwise have, so the question is whether the return exposure is bounded tightly enough to be worth it.",
    majorTerms: [
      "Return window and cut-off date",
      "Percentage of the order eligible for return, if capped",
      "Condition requirements for returned product",
      "Who pays return freight and handling",
      "Whether return is by credit, deduction or physical return",
      "Restocking charge, if any",
      "Minimum remaining shelf life for returned goods",
      "Return authorisation process",
      "Effect on revenue recognition and reserves",
      "Term and how the arrangement ends",
    ],
    clausesToWatch: [
      {
        clause: "Return window length",
        why: "A window longer than your shelf life means returned product arrives with no saleable life left and is a write-off rather than recovered inventory.",
      },
      {
        clause: "Cap on returnable quantity",
        why: "Without a cap, an entire order can come back, which makes the initial sale meaningless and leaves you with the production cost.",
      },
      {
        clause: "Condition of returned goods",
        why: "If damaged, opened or out-of-code product can be returned for full credit, the return right becomes a disposal service you are funding.",
      },
      {
        clause: "Return by deduction",
        why: "A retailer taking the credit as a deduction rather than physically returning product means you pay for goods you never get back and cannot verify.",
      },
      {
        clause: "Return freight responsibility",
        why: "Freight on a return of slow-moving product can exceed the value of the goods being returned.",
      },
      {
        clause: "Authorisation process",
        why: "Without a return authorisation step, product simply arrives at your dock with a credit already deducted and no way to dispute the quantity or condition.",
      },
    ],
    cpgIssues: [
      "The return window has to sit well inside remaining shelf life, or returned product is unsaleable on arrival and the recovery is zero.",
      "Returned food and beverage frequently cannot be resold at all once it has left your control, so treat the return exposure as a full write-off in your model, not as inventory recovered.",
      "Destroy-in-field arrangements, where the retailer disposes of product and takes the credit, remove your ability to verify quantity or condition entirely.",
      "Seasonal and holiday items carry the largest return exposure because the sell-through window is short and what does not move is worthless immediately after.",
      "Returned product handling raises food safety questions: product that has been in an uncontrolled environment should not go back into saleable inventory.",
      "Reserves for expected returns affect your reported revenue and your cash planning, so a broad return right changes the numbers you show investors and lenders.",
    ],
    founderMistakes: [
      "Agreeing an open return right to win a launch, then receiving most of the order back after the season with no saleable life remaining.",
      "Treating the initial shipment as recognised revenue without any reserve, and restating when the returns arrived.",
      "Accepting destroy-in-field credits with no verification, and paying for quantities they could not confirm.",
      "Paying return freight on low-value product where the freight exceeded the value of the goods.",
      "Setting a return window longer than the product's shelf life, so everything returned was automatically a write-off.",
    ],
    negotiationPoints: [
      "Cap the returnable quantity as a stated share of the order, and set a hard cut-off date well inside shelf life.",
      "Require a return authorisation before any product is returned or any credit is taken.",
      "Require returned product to be in saleable condition and within a stated remaining code.",
      "Agree who pays return freight, and consider a restocking charge that reflects your real handling cost.",
      "Prefer physical return with verification over destroy-in-field credit where the value justifies it.",
      "Model the sale with a full write-off assumption on returned units rather than assuming resale.",
    ],
    redFlags: [
      "An unlimited or uncapped return right.",
      "A return window longer than the product's remaining shelf life.",
      "Credits taken by deduction with no authorisation and no verification.",
      "Return of damaged, opened or out-of-code product for full credit.",
      "Return freight and handling entirely at your cost with no cap.",
      "No effect on the retailer of ordering far more than it can sell.",
    ],
    beforeYouSign: [
      "A model of the order assuming the maximum permitted return, written off entirely.",
      "Your product's remaining shelf life at the point the return window would close.",
      "A written return authorisation and verification process.",
    ],
    related: [
      "consignment-agreement",
      "retail-vendor-agreement",
      "wholesale-supply-agreement",
      "retailer-supply-agreement",
      "promotional-allowance-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "promotional-allowance-agreement",
    name: "Promotional Allowance Agreement",
    category: "retail",
    subcategory: "Trade spend and deductions",
    aliases: [
      "Trade Promotion Agreement",
      "Allowance Agreement",
      "Scan-Down Agreement",
      "Trade Spend Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Retailer or Distributor",
    whenYouNeedIt:
      "Whenever you fund a price promotion, an ad feature, a display or a temporary price reduction. Each deal should be papered before it runs, not reconstructed from the deduction that arrives afterwards.",
    whatItDoes:
      "Documents a specific promotional program: which items, which stores, which dates, the funding mechanic, the amount, how performance is evidenced, and how the money is actually taken — off invoice, as a bill-back, as a scan-down or as a lump sum.",
    whyItMatters:
      "Trade spend is usually the largest line between gross revenue and what actually lands in your bank account, and it is the least controlled because it is agreed verbally and collected by deduction months later. The mechanic matters as much as the amount: an off-invoice deduction comes off every case shipped whether it sells or not, while a scan-down pays only on units actually scanned. Fund the same promotion on the wrong mechanic and you can pay several times what you budgeted, and find out two quarters later.",
    majorTerms: [
      "Items, stores and exact promotional dates",
      "Funding mechanic: off-invoice, bill-back, scan-down or lump sum",
      "Allowance amount per case or per unit",
      "Estimated volume and any cap on total spend",
      "Performance requirements: feature, display, shelf price",
      "Proof of performance and how it is evidenced",
      "Deduction timing and reconciliation",
      "Treatment of unsold promotional inventory",
      "Dispute and clawback rights for non-performance",
      "Whether the allowance recurs or is one-time",
    ],
    clausesToWatch: [
      {
        clause: "Funding mechanic",
        why: "Off-invoice funds every case shipped regardless of sell-through, while scan-down funds only units sold, and the difference on a slow promotion is very large.",
      },
      {
        clause: "Total spend cap",
        why: "An uncapped per-case allowance against an optimistic volume forecast means your promotional budget is set by how much the retailer chooses to order.",
      },
      {
        clause: "Proof of performance",
        why: "Without evidence that the feature, display or shelf price actually happened, you can fund a promotion that never ran.",
      },
      {
        clause: "Clawback for non-performance",
        why: "If there is no right to recover funding when the agreed activity did not happen, proof of performance is information rather than leverage.",
      },
      {
        clause: "Forward-buy on promotional pricing",
        why: "A retailer buying deep at the promotional price and selling it after the promotion ends captures your funding as margin and depresses your next cycle.",
      },
      {
        clause: "Deduction timing and identification",
        why: "Deductions arriving months later with unclear references are almost impossible to match to the deal they relate to, which is how invalid ones survive.",
      },
    ],
    cpgIssues: [
      "Off-invoice allowances invite forward-buying, where a retailer loads up at the promotional price and sells through at full retail afterwards, so cap the quantity eligible for the deal price.",
      "Scan-down and bill-back mechanics are slower to settle but tie your money to actual consumer purchases, which is usually the right trade for a small brand.",
      "Deep promotions on short-shelf-life product can pull forward demand and leave aged inventory in the pipeline once the deal ends.",
      "Ad features and display placements are often sold as part of a promotional package but are separate commitments; get the feature dates and display type in writing.",
      "Deductions for promotional allowances typically arrive well after the promotion, so cash planning has to allow for a lag of one or more cycles.",
      "Promotions run through a distributor arrive as bill-backs from the distributor rather than the retailer, which adds a validation step you should insist on.",
      "Free fill and sample product supplied alongside a promotion should be counted in the total cost of the program, not treated separately.",
    ],
    founderMistakes: [
      "Agreeing an off-invoice allowance with no volume cap, and funding a large forward-buy that suppressed the following quarter entirely.",
      "Funding an ad feature that never ran, with no proof of performance requirement and no clawback right.",
      "Confirming promotions verbally with a buyer and then being unable to match or dispute the deductions that arrived four months later.",
      "Budgeting trade spend as a percentage of forecast and then having no cap when actual orders came in far higher.",
      "Running a deep promotion on a short-coded item and being left with aged inventory in the distributor's warehouse afterwards.",
    ],
    negotiationPoints: [
      "Paper every deal before it runs, with items, stores, dates, mechanic and amount stated.",
      "Prefer scan-down or bill-back over off-invoice where the mechanic is negotiable.",
      "Cap total promotional spend and the quantity eligible for the deal price.",
      "Require proof of performance and reserve a clawback right if the agreed activity did not run.",
      "Ask for deduction references that identify the specific promotion, and a reconciliation window.",
      "Agree how unsold promotional inventory is handled before the promotion starts.",
    ],
    redFlags: [
      "A promotional commitment agreed verbally with nothing in writing.",
      "Off-invoice funding with no cap on eligible volume.",
      "No proof of performance requirement.",
      "No clawback right when the agreed feature or display did not happen.",
      "Deductions with no promotion reference and no reconciliation process.",
      "A promotional calendar committed for a year in advance with no volume caps.",
    ],
    beforeYouSign: [
      "A written deal sheet with items, stores, dates, mechanic, rate and total cap.",
      "Your margin model for the promotion at the expected and the maximum volume.",
      "The proof of performance you will receive, and when.",
    ],
    related: [
      "retail-vendor-agreement",
      "slotting-placement-agreement",
      "retail-marketing-agreement",
      "food-distributor-agreement",
      "retail-broker-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "retail-marketing-agreement",
    name: "Retail Marketing Agreement",
    category: "retail",
    subcategory: "Trade spend and deductions",
    aliases: [
      "Retail Media Agreement",
      "In-Store Marketing Agreement",
      "Co-Op Advertising Agreement",
      "Joint Business Plan",
    ],
    stages: ["distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Retailer or Retailer Media Arm",
    whenYouNeedIt:
      "When a retailer offers marketing you pay for: in-store signage, endcaps, circular features, on-site digital advertising, loyalty offers or a retail media package, usually bundled into an annual plan.",
    whatItDoes:
      "Commits you to a package of retailer-run marketing activity for a stated fee, and sets what you get in return: placements, dates, impressions or displays, plus what reporting you receive and what happens if the activity does not run.",
    whyItMatters:
      "Retail marketing packages are sold as growth investment and collected as a deduction, which makes them easy to agree to and hard to evaluate. The two questions that matter are whether the activity is actually incremental to what you would already get, and whether you receive reporting good enough to tell. An annual commitment agreed at a category review can consume the entire marketing budget of a small brand before a single consumer sees anything.",
    majorTerms: [
      "Specific placements, formats and dates included",
      "Total fee and payment or deduction method",
      "Whether the commitment is annual or per-activity",
      "Performance metrics and reporting provided",
      "Make-good rights if activity does not run",
      "Creative approval and asset specifications",
      "Ownership and permitted reuse of creative assets",
      "Relationship to shelf placement and item authorisation",
      "Cancellation rights and notice",
      "Data and reporting access",
    ],
    clausesToWatch: [
      {
        clause: "Specificity of what you are buying",
        why: "A package described as marketing support with no listed placements or dates is a fee with no deliverable attached.",
      },
      {
        clause: "Make-good rights",
        why: "Without a make-good or refund right, activity that does not run is simply money spent, and you will find out from a report rather than in time to act.",
      },
      {
        clause: "Link between marketing spend and shelf space",
        why: "Where a marketing commitment is presented as influencing item authorisation or placement, the real cost of shelf is higher than the slotting number alone.",
      },
      {
        clause: "Annual commitment and cancellation",
        why: "A full-year commitment locks your entire trade marketing budget to one retailer before you know whether the item performs.",
      },
      {
        clause: "Reporting and measurement",
        why: "Without agreed metrics and reporting you cannot tell whether the spend produced anything, which makes the next renewal a guess.",
      },
      {
        clause: "Creative ownership and reuse",
        why: "Assets produced for a retailer program may be restricted to that retailer, so you can pay for creative you cannot use anywhere else.",
      },
    ],
    cpgIssues: [
      "In-store activity has to be timed to inventory: an endcap or feature running while the item is out of stock at store level wastes the entire spend.",
      "Demo and sampling elements bundled into a marketing package draw down real product, so add the cost of goods to the fee when evaluating it.",
      "Retail media and loyalty offers usually fund a consumer discount as well as a placement fee, so the total cost per unit is higher than the package price suggests.",
      "Display and endcap programs often require a specific shipper or display-ready pack, which is a separate packaging cost with its own minimum order.",
      "Marketing commitments made at a category review sit alongside slotting and promotional commitments made in the same meeting, and the combined total is what actually hits your margin.",
      "Reporting from retail media programs varies enormously in quality; for a small brand the practical measure is usually store-level sell-through during and after the activity.",
    ],
    founderMistakes: [
      "Committing an annual marketing package at a category review, then having no budget left when the item needed promotional support to survive a reset.",
      "Paying for an endcap that ran while the item was out of stock in most of the stores.",
      "Buying a display program without budgeting the display-ready packaging, which carried its own minimum order and lead time.",
      "Accepting a package with no listed placements, and having no basis to claim a make-good when little of it ran.",
      "Paying for creative production restricted to that retailer, and having to produce assets again for every other channel.",
    ],
    negotiationPoints: [
      "Require a schedule listing every placement, format and date, attached to the agreement.",
      "Ask for make-good or refund rights when committed activity does not run.",
      "Buy per-activity rather than annually until you have evidence the spend works.",
      "Agree the reporting and metrics you will receive, and when.",
      "Confirm the ownership and permitted reuse of any creative you pay to produce.",
      "Coordinate the timing of in-store activity with your inventory position and production schedule.",
    ],
    redFlags: [
      "A marketing fee with no listed placements, formats or dates.",
      "No make-good or refund right for activity that does not run.",
      "An annual commitment required before the item has any sales history.",
      "Marketing spend presented as a condition of keeping shelf placement.",
      "No reporting obligation of any kind.",
      "Creative you pay for that you may not use in any other channel.",
    ],
    beforeYouSign: [
      "A schedule of the exact placements, formats and dates included in the package.",
      "Your total committed spend with this retailer across slotting, promotions and marketing added together.",
      "Confirmation that inventory and production can support the activity dates.",
    ],
    related: [
      "promotional-allowance-agreement",
      "retail-vendor-agreement",
      "slotting-placement-agreement",
      "demo-agreement",
      "marketing-agency-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "slotting-placement-agreement",
    name: "Slotting / Placement Agreement",
    category: "retail",
    subcategory: "Trade spend and deductions",
    aliases: [
      "Slotting Fee Agreement",
      "New Item Placement Agreement",
      "Shelf Placement Agreement",
      "Pay-to-Stay Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Retailer or Distributor",
    whenYouNeedIt:
      "When a retailer or distributor asks for payment to list a new item, to hold a position on the shelf, or to open a set of stores. It usually arrives as a line in the new-item paperwork rather than as a separate negotiation.",
    whatItDoes:
      "Documents what you are paying for placement: which items, how many stores, which shelf position or set, for how long, and what happens if the item is delisted or the placement changes before the period you paid for is over.",
    whyItMatters:
      "Slotting is cash paid before a single unit sells, and it is the most common way a small brand spends its production budget on an outcome it does not control. Practice varies enormously by retailer, channel and category — some charge nothing, some charge a great deal — but the structural problem is the same everywhere: you pay up front for a placement that can be reviewed away at the next reset. What protects you is not the amount, it is the commitment attached to it and what happens if the item is dropped early.",
    majorTerms: [
      "Items and store count covered",
      "Fee amount and how it is collected",
      "Placement commitment: shelf position, facings and set",
      "Duration of the commitment and review dates",
      "Free fill obligation alongside the fee",
      "Refund or pro-rata treatment if the item is delisted early",
      "Whether the fee recurs at reset or is one-time",
      "Performance thresholds required to keep the placement",
      "New-store and expansion treatment",
      "Relationship to promotional and marketing commitments",
    ],
    clausesToWatch: [
      {
        clause: "What the fee actually commits the retailer to",
        why: "A fee paid with no stated placement duration or store count buys an intention, and the item can be reviewed out at the next reset with nothing owed back.",
      },
      {
        clause: "Refund on early delisting",
        why: "Without a pro-rata refund or credit, being dropped three months into a twelve-month placement means the full fee bought a quarter of the shelf time.",
      },
      {
        clause: "Free fill obligation",
        why: "Free fill sits alongside the fee and is often larger than it; a full chain fill can be an entire production run given away on top of the cash.",
      },
      {
        clause: "Recurring pay-to-stay",
        why: "A fee that recurs at every reset turns a one-time cost into a permanent tax on the listing that has to be earned back every cycle.",
      },
      {
        clause: "Performance thresholds",
        why: "If the item can be delisted for missing a velocity threshold you were never told, you have paid for a placement with an undisclosed condition attached.",
      },
      {
        clause: "New-store expansion",
        why: "Additional fees and free fill for every new store opening means a growing chain keeps drawing cash from you without any new commitment.",
      },
    ],
    cpgIssues: [
      "Free fill across a full chain is frequently the largest single cost of a launch and is easy to underestimate: multiply cases per store by store count at your cost of goods before you agree.",
      "Slotting and free fill together often exceed the gross margin the item will produce in its first year, which is why phased regional rollouts are usually the better structure for a small brand.",
      "Shelf position matters more than the number of facings for velocity, so the commitment should describe the set and position, not just that the item will be carried.",
      "Reset calendars determine how long you actually hold the placement, so the commitment period should be measured against the next scheduled reset for the category.",
      "Distributors also charge listing and warehouse slotting fees, and those are separate from the retailer's, so the true cost of entering a chain can include both.",
      "Velocity thresholds are usually measured against category benchmarks you will not see in advance; ask what the item has to do to survive the first review.",
      "A delisted item leaves warehouse and store inventory that may come back to you, so the cost of a failed listing includes the return, not just the fee.",
    ],
    founderMistakes: [
      "Paying a chain-wide slotting fee and free fill from a single production run, leaving no inventory or cash to support the item once it was on shelf.",
      "Not asking what the placement commitment actually was, and being delisted at the first reset with no refund and no recourse.",
      "Budgeting the fee but not the free fill, which turned out to be several times larger.",
      "Agreeing to slotting at both the retailer and the distributor without realising these were separate charges for the same listing.",
      "Spending the entire launch budget on placement and having nothing left for the demos and promotions that drive the velocity needed to keep it.",
    ],
    negotiationPoints: [
      "Ask for a stated placement period and store count in writing, and a pro-rata refund or credit if the item is delisted early.",
      "Propose a phased regional rollout rather than a full chain launch, to stage both fee and free fill.",
      "Offer performance-based alternatives where they are entertained: a higher promotional commitment or a scan-down in place of cash up front.",
      "Confirm whether the fee recurs at reset and budget accordingly.",
      "Ask what velocity threshold the item must meet to hold placement at the first review.",
      "Model slotting, free fill, distributor listing fees and first-year promotional spend as one number before agreeing any part of it.",
    ],
    redFlags: [
      "A fee with no stated placement period, store count or position commitment.",
      "No refund or credit if the item is delisted before the period ends.",
      "Free fill scope that exceeds your production capacity or your cash.",
      "A recurring pay-to-stay charge at every reset.",
      "Undisclosed velocity thresholds governing whether you keep the placement.",
      "Fees demanded at both the retailer and the distributor for the same listing, discovered late.",
    ],
    beforeYouSign: [
      "The total cash and product cost of entry: slotting, free fill, distributor listing fees and first-year promotional commitments together.",
      "A written statement of the placement period, store count and shelf position you are buying.",
      "The next category reset date and what the item has to achieve before it.",
      "Confirmation your production and cash can support the fill volume and the follow-on inventory.",
    ],
    related: [
      "retail-vendor-agreement",
      "promotional-allowance-agreement",
      "retail-marketing-agreement",
      "food-distributor-agreement",
      "retail-broker-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "demo-agreement",
    name: "Demo Agreement",
    category: "retail",
    subcategory: "In-store activity",
    aliases: [
      "In-Store Demo Agreement",
      "Product Demonstration Agreement",
      "Retail Demo Program Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Retailer or Demo Program Provider",
    whenYouNeedIt:
      "When you run in-store demonstrations, whether through the retailer's own demo program, a third-party demo company, or your own staff working inside someone else's store.",
    whatItDoes:
      "Sets the terms for demonstration activity in a retailer's stores: which stores and dates, who staffs and supervises the demonstrators, who supplies product and equipment, what the fee is, and who carries the insurance and the liability for what happens on the retail floor.",
    whyItMatters:
      "Demos are one of the few reliable ways a small brand builds velocity, and they are also the point where your product is prepared and handed to the public by someone you may not employ, inside a store you do not control. The commercial risk is that demo fees plus product cost exceed the sales lift; the practical risk is food handling and allergen disclosure at the table. Both need to be assigned to someone in writing before the first demo runs.",
    majorTerms: [
      "Stores, dates and number of demo hours",
      "Fee per demo or per hour, and what it includes",
      "Who supplies and pays for demo product",
      "Equipment, table, signage and sampling supplies",
      "Staffing: retailer program, third-party agency or brand staff",
      "Food handling, hygiene and certification requirements",
      "Allergen disclosure and ingredient information at the table",
      "Insurance and additional insured requirements",
      "Liability for incidents on the retail floor",
      "Reporting: units sampled, sales lift and store feedback",
      "Cancellation, no-show and rescheduling terms",
    ],
    clausesToWatch: [
      {
        clause: "Who employs and supervises the demonstrator",
        why: "If your own staff work inside the store, the responsibility for their conduct and their safety needs to be assigned rather than assumed.",
      },
      {
        clause: "Food handling and certification requirements",
        why: "Requirements for handling and preparing food samples in a retail environment vary by location and by retailer, and the agreement should say who is responsible for meeting them.",
      },
      {
        clause: "Allergen disclosure at the sampling table",
        why: "Handing a prepared sample to a member of the public without ingredient and allergen information available is a real safety issue and an obvious liability question.",
      },
      {
        clause: "Insurance and additional insured status",
        why: "Retailers commonly require specific limits and endorsements for anyone operating on the sales floor, and an unverified promise leaves the gap with you.",
      },
      {
        clause: "Cancellation and no-show terms",
        why: "Paying for demos that did not happen, or being charged for cancellations outside your control, quietly erodes a small activation budget.",
      },
      {
        clause: "Reporting obligations",
        why: "Without units sampled and store-level sales data you cannot tell which stores are worth returning to, which is the only way demo spend improves over time.",
      },
    ],
    cpgIssues: [
      "Demo product is cost of goods given away, so the true cost of a demo is the fee plus the cases consumed, and that total is what has to be measured against sell-through.",
      "Allergen and ingredient information must be available at the table for anything sampled, particularly for products containing common allergens.",
      "Food handling requirements for opening, portioning and serving samples in-store vary by location and retailer, and someone has to be responsible for meeting them.",
      "Chilled and frozen products need temperature control at the demo table, and a sampling setup without it is a food safety problem rather than a logistics inconvenience.",
      "Sample cups, utensils, ice and disposal are real per-demo costs that founders leave out of the model entirely.",
      "Product used for demos still needs to be within code and lot-traceable, so demo stock should be drawn and recorded like any other shipment.",
      "Demo scheduling has to align with in-store inventory, because a demo run while the item is out of stock converts interest into nothing.",
    ],
    founderMistakes: [
      "Booking a run of demos and measuring only the fee, ignoring that product cost roughly doubled the true spend.",
      "Running demos in stores that were out of stock, and generating interest with nothing on the shelf to buy.",
      "Sending staff to sample product with no allergen or ingredient information available at the table.",
      "Not checking the retailer's insurance requirements for anyone working on the sales floor until the demonstrator was turned away.",
      "Getting no store-level reporting, so the same underperforming stores were booked again the following quarter.",
    ],
    negotiationPoints: [
      "Agree the full per-demo cost including product, supplies and fee before booking a schedule.",
      "Assign responsibility for food handling requirements, hygiene and allergen information in writing.",
      "Confirm insurance limits and endorsements required by the retailer before the first demo.",
      "Require units-sampled and store-level sales reporting so you can compare stores.",
      "Set clear cancellation and rescheduling terms, including what happens if the store is out of stock.",
      "Schedule demos against confirmed store inventory rather than a calendar.",
    ],
    redFlags: [
      "No allocation of responsibility for food handling or allergen disclosure.",
      "No insurance requirement stated, or limits you have not verified against your policy.",
      "Fees payable regardless of whether the demo ran or the store had stock.",
      "No reporting on units sampled or sales lift.",
      "Demo staffing arrangements that leave supervision and responsibility unassigned.",
      "Temperature-sensitive product sampled with no cold-chain provision.",
    ],
    beforeYouSign: [
      "A full per-demo cost model including product, supplies and fees.",
      "The retailer's insurance and floor-access requirements, checked against your policy.",
      "Confirmed in-store inventory for the demo dates.",
      "A written allergen and ingredient information sheet for the demo table.",
    ],
    related: [
      "sampling-agreement",
      "demo-contractor-agreement",
      "retail-marketing-agreement",
      "retail-vendor-agreement",
      "event-sampling-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "sampling-agreement",
    name: "Sampling Agreement",
    category: "retail",
    subcategory: "In-store activity",
    aliases: [
      "Product Sampling Agreement",
      "Sampling Program Agreement",
      "Trial Program Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Retailer, Sampling Partner or Program Operator",
    whenYouNeedIt:
      "When your product is distributed as samples through someone else's program: a retailer's sampling box, a subscription insert, a gym or campus program, or a partner bundling your product with theirs.",
    whatItDoes:
      "Sets the terms for supplying product into a sampling program: how many units, in what format, delivered where and when, at whose cost, with what information accompanying the sample and who is responsible if something goes wrong with it.",
    whyItMatters:
      "Sampling programs are sold on reach and paid for in product, and the cost of goods on a large program can equal a full production run. The commercial question is whether you get anything measurable back — data, conversion, a coupon redemption path — or just a number of units shipped. The safety question is that a sample reaching a consumer outside a store carries the same labelling, allergen and traceability obligations as anything you sell, and often with less control over how it is stored and handled.",
    majorTerms: [
      "Number of units, format and pack size",
      "Delivery location, date and freight responsibility",
      "Program fee in addition to product cost",
      "Labelling and information accompanying the sample",
      "Storage and handling conditions during the program",
      "Distribution method and audience",
      "Reporting: units distributed, redemption and feedback data",
      "Rights to use consumer data collected",
      "Brand and trademark use in program materials",
      "Liability, insurance and recall cooperation",
      "Unused product return or disposal",
    ],
    clausesToWatch: [
      {
        clause: "What you get back from the program",
        why: "A program that reports only units shipped tells you nothing, and without conversion or redemption data you cannot evaluate whether to do it again.",
      },
      {
        clause: "Storage and handling during the program",
        why: "Samples held in uncontrolled conditions can reach consumers degraded or spoiled, and the product still carries your name.",
      },
      {
        clause: "Labelling and accompanying information",
        why: "A sample in a non-retail format still needs to carry ingredient and allergen information, and it is easy to lose that when product is repacked into a program.",
      },
      {
        clause: "Consumer data rights",
        why: "If the program owns all data collected, you paid in product for reach and received nothing you can use afterwards.",
      },
      {
        clause: "Recall and withdrawal cooperation",
        why: "Sampled units can be the hardest to trace, so the program operator's obligation to keep distribution records matters more here than in retail.",
      },
      {
        clause: "Brand use in program materials",
        why: "Without approval rights your brand can appear alongside claims or partners you would not have chosen.",
      },
    ],
    cpgIssues: [
      "Sample and trial formats often need their own packaging run with its own minimum order and lead time, which is a cost separate from the product itself.",
      "Any sample reaching a consumer needs ingredient and allergen information with it, whatever the format, and repacking into a program box is where that gets lost.",
      "Lot codes must be recorded for units entering a sampling program, because a withdrawal has to be able to reach them.",
      "Shelf life on sampled product should allow for time sitting in a program warehouse before it reaches a consumer, not just transit time.",
      "Chilled or temperature-sensitive product is a poor fit for most sampling programs unless the cold chain is specified and verified end to end.",
      "The real cost is cost of goods plus program fee plus freight plus any special packaging, and it should be measured against redemption or conversion rather than impressions.",
      "Coupon or discount codes tied to a sampling program are the only reliable way most small brands measure conversion, so build the redemption path in before shipping product.",
    ],
    founderMistakes: [
      "Shipping a large sampling volume against a promise of reach, and receiving no data at all beyond a count of units distributed.",
      "Losing allergen and ingredient information when product was repacked into a program's own box.",
      "Not building a trackable redemption path, so there was no way to attribute any subsequent sales to the program.",
      "Sending product with shelf life that expired while it sat in the program operator's warehouse.",
      "Budgeting only cost of goods and being surprised by the program fee, freight and a separate sample-format packaging run.",
    ],
    negotiationPoints: [
      "Agree the reporting you will receive, including units distributed and any redemption or conversion data, before shipping.",
      "Require ingredient and allergen information to accompany every sample in whatever format it is distributed.",
      "Specify storage conditions and a maximum time product may be held before distribution.",
      "Build in a trackable coupon or code so conversion can be measured.",
      "Agree rights to any consumer data collected through the program.",
      "Require lot-code records and recall cooperation from the program operator.",
      "Confirm brand and trademark use is subject to your approval.",
    ],
    redFlags: [
      "No reporting beyond a count of units shipped.",
      "No obligation to keep ingredient and allergen information with the sample.",
      "No storage condition or holding time limit for your product.",
      "The program operator owning all consumer data collected.",
      "No lot-code records or recall cooperation obligation.",
      "Unrestricted use of your brand in program materials.",
    ],
    beforeYouSign: [
      "A total cost model: cost of goods, sample packaging, freight and program fee.",
      "The reporting package you will receive, in writing.",
      "A redemption or tracking mechanism built into the sample.",
      "Confirmation of how and where product will be stored before it reaches consumers.",
    ],
    related: [
      "demo-agreement",
      "event-sampling-agreement",
      "product-sampling-release",
      "retail-marketing-agreement",
      "sampling-agency-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default RETAIL_DOCS;
